/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/Manual/FinalizeWIFEndpointComponent" />
import React = require("react");
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { EndpointsEditorActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsCreator";
import { NavigationActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/NavigationActionsCreator";
import { GenericAuthorizationComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericAuthorizationComponent";
import { ActionsCreatorInstanceIds, TestConnectionDataSource } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { EndpointsEditorStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointsEditorStore";
import { AzureEndpointConstants } from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { InputDescriptor } from "ServiceEndpoints/Client/Contracts/FormInput_ServiceEndpoint";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService.Types";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { EndpointErrorMessageWidget } from "../Common/EndpointErrorMessageWidget";
import { FederationDetails } from "../Common/FederationDetails";
import { PipelinePermissionsCheckbox } from "../Common/PipelinePermissionsCheckbox";
import { ARMAuthenticationDetailsHeader } from "./ARMAuthenticationDetailsHeader";
import { WIFEndpointDraftMessageCard } from "./WIFEndpointDraftMessageCard";
import { WIFEndpointFooterComponent } from "./WIFEndpointFooterComponent";
import { SaveDraftEndpointButton } from "./SaveDraftEndpointButton";
import { EndpointSaveProgressStore, EndpointSaveProgressStoreEvents, createEndpointSaveProgressStore, deleteEndpointSaveProgressStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointSaveProgressStore";

interface IFinalizeWIFEndpointComponentState {
    enableSave: boolean;
    enableSaveAsDraft: boolean;
}

export class FinalizeWIFEndpointComponent extends VssComponent<{}, IFinalizeWIFEndpointComponentState>  {

    private _invalidInputCount = 0;
    private _malformedInputs: string[] = [];

    private readonly _endpointEditorStore: EndpointsEditorStore;
    private readonly _endpointEditorActionCreator: EndpointsEditorActionsCreator;
    private readonly _navigationActionsCreator: NavigationActionsCreator;
    private readonly _endpointSaveProgressStore: EndpointSaveProgressStore;

    constructor(context: IVssComponentContext) {
        super(context);

        this._endpointEditorStore = StoreManager.GetStore<EndpointsEditorStore>(EndpointsEditorStore, ActionsCreatorInstanceIds.EndpointsEditorStoreInstanceId);
        this._endpointEditorActionCreator = ActionCreatorManager.GetActionCreator<EndpointsEditorActionsCreator>(EndpointsEditorActionsCreator, ActionsCreatorInstanceIds.EndpointsEditorActionsCreatorInstanceId);
        this._navigationActionsCreator =  ActionCreatorManager.GetActionCreator<NavigationActionsCreator>(NavigationActionsCreator, ActionsCreatorInstanceIds.NavigationActionsCreatorInstanceId);

        this._endpointSaveProgressStore = createEndpointSaveProgressStore();
        this._endpointSaveProgressStore.addListener(EndpointSaveProgressStoreEvents.Completed, this._onSaveEndpointCompleted);

        this.state = {
            enableSave: false,
            enableSaveAsDraft: true
        };
    }

    public componentWillUnmount(): void {
        this._endpointSaveProgressStore.removeListener(EndpointSaveProgressStoreEvents.Succeeded, this._onEndpointSaveSuccess);
        this._endpointSaveProgressStore.removeListener(EndpointSaveProgressStoreEvents.Completed, this._onSaveEndpointCompleted);
        deleteEndpointSaveProgressStore(this._endpointSaveProgressStore);
    }

    public render(): React.ReactNode {

        const endpoint = this._endpointEditorStore.getEndpointClone();
        const endpointType = this._endpointEditorStore.getState().serviceEndpointType;

        return (<React.Fragment>
            <WIFEndpointDraftMessageCard />

            <div role="heading" aria-level={4} className="service-endpoint-step-header">{Resources.WorkloadIdentityFederationStep2Title2}</div>

            <FederationDetails />

            <GenericAuthorizationComponent
                disableAllPipelinesSetting={false}
                serviceEndpointType={endpointType}
                draftEndpoint={endpoint}
                isUpdateForm={false}
                updateDraftEndpointState={this._updateEndpointState}
                updateInvalidInputsCount={this._updateInvalidInputsCount}
                onValidationChange={this._onValidationChange}
                renderAuthenticationHeader={() => <ARMAuthenticationDetailsHeader />}
            />

            <PipelinePermissionsCheckbox />

            <EndpointErrorMessageWidget />

            <WIFEndpointFooterComponent renderButtons={this._renderButtonSection} />

        </React.Fragment>);
    }

    private _renderButtonSection = (): JSX.Element => {
        return (<React.Fragment>
            <SaveDraftEndpointButton closeUponSuccess={true} disabled={!this.state.enableSaveAsDraft} isUpdateFlow={true} />
            <Button
                disabled={!this.state.enableSave}
                primary
                text={Resources.VerifyAndSaveText}
                onClick={this._saveEndpoint}
            />
        </React.Fragment>);

    }

    private _saveEndpoint = (): void => {
        const isOpenToAllPipelines = this._endpointEditorStore.isOpenToAllPipelines();
        const endpoint = this._endpointEditorStore.getEndpointClone();
        delete endpoint.data[AzureEndpointConstants.IsDraft];
        this._endpointSaveProgressStore.addListener(EndpointSaveProgressStoreEvents.Succeeded, this._onEndpointSaveSuccess);
        this._endpointEditorActionCreator.verifyAndSaveServiceEndpoint(endpoint, TestConnectionDataSource, isOpenToAllPipelines, true);
    }

    private _updateInvalidInputsCount = (invalidInputCount: number): void => {
        this._invalidInputCount += invalidInputCount;
        this.setState({
            enableSave: this._invalidInputCount === 0
        });
    }

    private _onValidationChange = (descriptor: InputDescriptor, isValid: boolean, isPresent: boolean): void => {
        const malformedInputs = new Set<string>(this._malformedInputs);
        if (!isValid && isPresent) {
            malformedInputs.add(descriptor.id);
        }
        else {
            malformedInputs.delete(descriptor.id);
        }
        this._malformedInputs = [...malformedInputs];

        this.setState({
            enableSaveAsDraft: this._malformedInputs.length === 0
        });
    }

    private _updateEndpointState = (endpoint: ServiceEndpoint): void => {
        this._endpointEditorActionCreator.setServiceEndpoint(endpoint);
    }

    private showSuccessToast() {
        const messagesService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        messagesService.addToast({
            message: Resources.ServiceEndpointConfigurationSuccessful,
            duration: 10000,
            forceOverrideExisting: true
        });
    }

    private _onSaveEndpointCompleted = (): void => {
        this._endpointSaveProgressStore.removeListener(EndpointSaveProgressStoreEvents.Succeeded, this._onEndpointSaveSuccess);
    }

    private _onEndpointSaveSuccess = (): void => {
        this.showSuccessToast();
        this._navigationActionsCreator.closePanel();
    }
}